import { useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../App";
import { getChatsPolling, getChatDetailsPolling } from "../pages/Chats/actions";

interface UseChatPollingProps {
  currentChatId?: string;
  isEnabled?: boolean;
}

export const useChatPolling = ({ 
  currentChatId, 
  isEnabled = true 
}: UseChatPollingProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const chatListIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const activeChatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isVisibleRef = useRef(true);

  // Handle page visibility changes
  const handleVisibilityChange = useCallback(() => {
    isVisibleRef.current = !document.hidden;
    
    if (document.hidden) {
      // Pause polling when tab is hidden
      if (chatListIntervalRef.current) {
        clearInterval(chatListIntervalRef.current);
        chatListIntervalRef.current = null;
      }
      if (activeChatIntervalRef.current) {
        clearInterval(activeChatIntervalRef.current);
        activeChatIntervalRef.current = null;
      }
    } else if (isEnabled) {
      // Resume polling when tab becomes visible
      startPolling();
    }
  }, [isEnabled]);

  // Start polling function
  const startPolling = useCallback(() => {
    if (!isEnabled || !isVisibleRef.current) return;

    // Chat list polling - every 60 seconds
    if (!chatListIntervalRef.current) {
      chatListIntervalRef.current = setInterval(() => {
        if (isVisibleRef.current) {
          dispatch(getChatsPolling());
        }
      }, 60000); // 60 seconds
    }

    // Active chat polling - every 30 seconds (only if chat is open)
    if (currentChatId && !activeChatIntervalRef.current) {
      activeChatIntervalRef.current = setInterval(() => {
        if (isVisibleRef.current && currentChatId) {
          dispatch(getChatDetailsPolling(currentChatId));
        }
      }, 30000); // 30 seconds
    }
  }, [dispatch, currentChatId, isEnabled]);

  // Stop polling function
  const stopPolling = useCallback(() => {
    if (chatListIntervalRef.current) {
      clearInterval(chatListIntervalRef.current);
      chatListIntervalRef.current = null;
    }
    if (activeChatIntervalRef.current) {
      clearInterval(activeChatIntervalRef.current);
      activeChatIntervalRef.current = null;
    }
  }, []);

  // Set up page visibility listener
  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  // Start/stop polling based on enabled state
  useEffect(() => {
    if (isEnabled && isVisibleRef.current) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [isEnabled, startPolling, stopPolling]);

  // Handle active chat changes
  useEffect(() => {
    if (activeChatIntervalRef.current) {
      clearInterval(activeChatIntervalRef.current);
      activeChatIntervalRef.current = null;
    }

    if (currentChatId && isEnabled && isVisibleRef.current) {
      activeChatIntervalRef.current = setInterval(() => {
        if (isVisibleRef.current && currentChatId) {
          dispatch(getChatDetailsPolling(currentChatId));
        }
      }, 30000); // 30 seconds
    }
  }, [dispatch, currentChatId, isEnabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    startPolling,
    stopPolling,
  };
};